import React, { useCallback, useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import {
  Alert,
  Button,
  Checkbox,
  Code,
  Group,
  Loader,
  Modal,
  Paper,
  SegmentedControl,
  Select,
  SimpleGrid,
  Stack,
  Table,
  Text,
  Tooltip,
} from '@mantine/core';
import { Eye, RefreshCw, RotateCw } from 'lucide-react';
import { Layout } from '../components/Layout.tsx';
import { useUrlState } from '../hooks/useUrlState.ts';
import { useAuth } from '../contexts/AuthContext.tsx';
import { odooQueueAPI, projectAPI } from '../services/api.ts';
import {
  ClientBadge,
  ConfirmModal,
  PageHeader,
  StatusBadge,
  TablePagination,
} from '../components/app/index.ts';
import { toast } from '../lib/toast.ts';

const PAGE_SIZE = 50;

// Failed first: it is the list an admin acts on.
const STATUSES = [
  { value: 'failed', label: 'Failed' },
  { value: 'queued', label: 'Queued' },
  { value: 'processing', label: 'Sending' },
  { value: 'synced', label: 'Sent' },
  { value: '', label: 'All' },
];

const TILES = [
  { key: 'failed', label: 'Failed', color: 'red' },
  { key: 'queued', label: 'Queued', color: 'blue' },
  { key: 'processing', label: 'Sending', color: 'yellow' },
  { key: 'synced', label: 'Sent', color: 'green' },
];

const errorOf = (error: any, fallback: string) =>
  error?.response?.data?.message || error?.message || fallback;

/**
 * The Odoo queue: every write the apps made that is waiting for Odoo, was
 * sent, or was refused. An admin sees their own project only; a super admin
 * sees every project and can narrow to one.
 */
export const OdooQueue = () => {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === 'super_admin';

  const [rows, setRows] = useState<any[]>([]);
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [queue, setQueue] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  // Kept in the address, so the list comes back as it was left.
  const [page, setPage] = useUrlState('page', 1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [status, setStatus] = useUrlState('status', 'failed', {
    resetPage: true,
  });
  const [projectId, setProjectId] = useUrlState('project', null, {
    resetPage: true,
  });
  const [projects, setProjects] = useState<{ value: string; label: string }[]>(
    [],
  );
  const [selected, setSelected] = useState<string[]>([]);
  const [retrying, setRetrying] = useState(false);
  const [confirmAll, setConfirmAll] = useState(false);
  const [viewing, setViewing] = useState<any>(null);

  const load = useCallback(
    async (quiet = false) => {
      try {
        if (!quiet) setLoading(true);
        const response = await odooQueueAPI.list({
          page,
          limit: PAGE_SIZE,
          ...(status && { status }),
          ...(projectId && { projectId }),
        });
        setRows(response.data || []);
        setCounts(response.counts || {});
        setQueue(response.queue);
        setTotalPages(response.pagination?.totalPages || 1);
        setTotalItems(response.pagination?.total || 0);
        setError('');
      } catch (err: any) {
        setError(errorOf(err, 'Failed to load the Odoo queue'));
      } finally {
        setLoading(false);
      }
    },
    [page, status, projectId],
  );

  useEffect(() => {
    load();
  }, [load]);

  // Selections belong to the list on screen.
  useEffect(() => {
    setSelected([]);
  }, [page, status, projectId]);

  // Keep the picture fresh while anything is still on its way.
  const moving = (counts.queued || 0) + (counts.processing || 0) > 0;
  useEffect(() => {
    if (!moving) return;
    const timer = setInterval(() => load(true), 5000);
    return () => clearInterval(timer);
  }, [moving, load]);

  useEffect(() => {
    if (!isSuperAdmin) return;
    projectAPI
      .getAll(1, 200)
      .then((response: any) => {
        const list = response?.data?.projects || response?.data || [];
        setProjects(
          (Array.isArray(list) ? list : []).map((project: any) => ({
            value: String(project.id),
            label: project.name,
          })),
        );
      })
      .catch(() => setProjects([]));
  }, [isSuperAdmin]);

  const failedOnPage = useMemo(
    () => rows.filter((row) => row.status === 'failed'),
    [rows],
  );
  const allSelected =
    failedOnPage.length > 0 && selected.length === failedOnPage.length;

  const retry = async (body: { ids?: string[]; all?: boolean }) => {
    try {
      setRetrying(true);
      const response = await odooQueueAPI.retry({
        ...body,
        ...(projectId && { projectId }),
      });
      toast.success(
        `${response.requeued} request${response.requeued === 1 ? '' : 's'} queued again`,
      );
      setSelected([]);
      setConfirmAll(false);
      await load(true);
    } catch (err: any) {
      toast.error(errorOf(err, 'Could not retry'));
    } finally {
      setRetrying(false);
    }
  };

  return (
    <Layout>
      <PageHeader
        title="Odoo Queue"
        description="Requests the apps made that are waiting for Odoo, were sent, or were refused."
        actions={
          <>
            <Button
              variant="default"
              leftSection={<RefreshCw size={16} />}
              onClick={() => load()}
            >
              Refresh
            </Button>
            <Button
              variant="light"
              leftSection={<RotateCw size={16} />}
              disabled={selected.length === 0}
              loading={retrying && !confirmAll}
              onClick={() => retry({ ids: selected })}
            >
              Retry selected ({selected.length})
            </Button>
            <Button
              leftSection={<RotateCw size={16} />}
              disabled={!counts.failed}
              onClick={() => setConfirmAll(true)}
            >
              Retry all failed
            </Button>
          </>
        }
      />

      <SimpleGrid cols={{ base: 2, md: 4 }} mb="lg">
        {TILES.map((tile) => (
          <Paper key={tile.key} withBorder shadow="sm" p="md">
            <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
              {tile.label}
            </Text>
            <Text fz={28} fw={700} c={tile.color}>
              {counts[tile.key] || 0}
            </Text>
          </Paper>
        ))}
      </SimpleGrid>

      {queue === null && !loading && (
        <Alert color="yellow" variant="light" mb="lg">
          The queue service is not reachable right now. Requests are still
          saved and will be sent once it is back.
        </Alert>
      )}

      <Paper withBorder shadow="sm" p={{ base: 'md', sm: 'lg' }}>
        <Stack gap="md">
          <Group justify="space-between" wrap="wrap" gap="sm">
            <SegmentedControl
              value={status}
              onChange={(value) => {
                setStatus(value);
                setPage(1);
              }}
              data={STATUSES}
            />
            {isSuperAdmin && (
              <Select
                placeholder="All projects"
                data={projects}
                value={projectId}
                onChange={(value) => {
                  setProjectId(value);
                  setPage(1);
                }}
                clearable
                searchable
                w={240}
              />
            )}
          </Group>

          {error && (
            <Alert
              color="red"
              variant="light"
              withCloseButton
              onClose={() => setError('')}
            >
              {error}
            </Alert>
          )}

          {loading ? (
            <Group justify="center" h={160}>
              <Loader />
            </Group>
          ) : (
            <>
              <Table.ScrollContainer minWidth={1080}>
                <Table withTableBorder>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th w={40}>
                        <Checkbox
                          aria-label="Select all failed on this page"
                          checked={allSelected}
                          indeterminate={selected.length > 0 && !allSelected}
                          disabled={failedOnPage.length === 0}
                          onChange={() =>
                            setSelected(
                              allSelected
                                ? []
                                : failedOnPage.map((row) => row._id),
                            )
                          }
                        />
                      </Table.Th>
                      <Table.Th>Created</Table.Th>
                      <Table.Th>Request</Table.Th>
                      <Table.Th>Employee</Table.Th>
                      <Table.Th>Source</Table.Th>
                      <Table.Th>Status</Table.Th>
                      <Table.Th>Tries</Table.Th>
                      <Table.Th>Error</Table.Th>
                      <Table.Th>Actions</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {rows.map((row) => (
                      <Table.Tr key={row._id}>
                        <Table.Td>
                          <Checkbox
                            aria-label="Select request"
                            checked={selected.includes(row._id)}
                            disabled={row.status !== 'failed'}
                            onChange={() =>
                              setSelected((current) =>
                                current.includes(row._id)
                                  ? current.filter((id) => id !== row._id)
                                  : [...current, row._id],
                              )
                            }
                          />
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm">
                            {dayjs(row.createdAt).format('DD/MM/YYYY HH:mm')}
                          </Text>
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm" fw={500} tt="capitalize">
                            {row.label}
                          </Text>
                          <Text size="xs" c="dimmed">
                            Job {String(row._id).slice(-6).toUpperCase()}
                          </Text>
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm">
                            {row.employeeName || row.employeeId || '—'}
                          </Text>
                        </Table.Td>
                        <Table.Td>
                          {/* Requests from before the source was recorded have none. */}
                          {row.clientSource ? (
                            <Stack gap={2}>
                              <ClientBadge client={row.clientSource} size="sm" />
                              {row.clientVersion && (
                                <Text size="xs" c="dimmed">
                                  {row.clientVersion}
                                </Text>
                              )}
                            </Stack>
                          ) : (
                            <Text size="sm" c="dimmed">
                              —
                            </Text>
                          )}
                        </Table.Td>
                        <Table.Td>
                          <StatusBadge
                            status={
                              row.status === 'synced'
                                ? 'sent'
                                : row.status === 'processing'
                                  ? 'sending'
                                  : row.status
                            }
                          />
                        </Table.Td>
                        <Table.Td>{row.attempts}</Table.Td>
                        <Table.Td maw={320}>
                          <Text size="sm" c="red" lineClamp={2}>
                            {row.lastError || ''}
                          </Text>
                        </Table.Td>
                        <Table.Td>
                          <Group gap={6} wrap="nowrap">
                            <Tooltip label="What was sent">
                              <Button
                                size="compact-xs"
                                variant="default"
                                leftSection={<Eye size={12} />}
                                onClick={() => setViewing(row)}
                              >
                                View
                              </Button>
                            </Tooltip>
                            {row.status === 'failed' && (
                              <Button
                                size="compact-xs"
                                variant="light"
                                leftSection={<RotateCw size={12} />}
                                disabled={retrying}
                                onClick={() => retry({ ids: [row._id] })}
                              >
                                Retry
                              </Button>
                            )}
                          </Group>
                        </Table.Td>
                      </Table.Tr>
                    ))}

                    {rows.length === 0 && (
                      <Table.Tr>
                        <Table.Td colSpan={9}>
                          <Text ta="center" c="dimmed" py="md">
                            {status === 'failed'
                              ? 'Nothing has failed.'
                              : 'No requests found.'}
                          </Text>
                        </Table.Td>
                      </Table.Tr>
                    )}
                  </Table.Tbody>
                </Table>
              </Table.ScrollContainer>

              <TablePagination
                page={page}
                totalPages={totalPages}
                onChange={setPage}
                totalItems={totalItems}
                pageSize={PAGE_SIZE}
              />
            </>
          )}
        </Stack>
      </Paper>

      <ConfirmModal
        opened={confirmAll}
        onClose={() => setConfirmAll(false)}
        onConfirm={() => retry({ all: true })}
        title="Retry every failed request?"
        confirmLabel="Retry all"
        color="green"
        loading={retrying}
      >
        {counts.failed || 0} failed request
        {counts.failed === 1 ? '' : 's'} will be sent to Odoo again, unchanged.
        Requests Odoo refused for their content will fail again until the
        employee edits them.
      </ConfirmModal>

      <Modal
        opened={!!viewing}
        onClose={() => setViewing(null)}
        title={<Text fw={600} tt="capitalize">{viewing?.label}</Text>}
        size="lg"
      >
        {viewing && (
          <Stack gap="sm">
            <Group gap="xs">
              <StatusBadge status={viewing.status === 'synced' ? 'sent' : viewing.status} />
              <Text size="sm" c="dimmed">
                {viewing.operation} · {viewing.attempts} tries ·{' '}
                {dayjs(viewing.updatedAt).format('DD/MM/YYYY HH:mm:ss')}
              </Text>
            </Group>
            {viewing.clientSource && (
              <Group gap="xs">
                <Text size="sm" c="dimmed">
                  Sent from
                </Text>
                <ClientBadge client={viewing.clientSource} size="sm" />
                {viewing.clientVersion && (
                  <Text size="sm" c="dimmed">
                    version {viewing.clientVersion}
                  </Text>
                )}
              </Group>
            )}
            {viewing.lastError && (
              <Alert color="red" variant="light">
                {viewing.lastError}
              </Alert>
            )}
            <Text size="sm" fw={500}>
              Sent to Odoo
            </Text>
            <Code block mah={320} style={{ overflow: 'auto' }}>
              {viewing.payload
                ? JSON.stringify(viewing.payload, null, 2)
                : 'Not sent yet.'}
            </Code>
          </Stack>
        )}
      </Modal>
    </Layout>
  );
};

export default OdooQueue;
